import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import MovieCard from './MovieCard';
import AddMovieModal from './AddMovieModal';
import { db } from '../firebase';
import { collection, query, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

const Dashboard = ({ user, onLogout, isGuest = false }) => {
  const [activeTab, setActiveTab] = useState('watched');
  const [movies, setMovies] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    // Use demoMovies collection for guest users, movies collection for real users
    const collectionName = isGuest ? 'demoMovies' : 'movies';
    const q = query(collection(db, collectionName));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const moviesData = [];
      querySnapshot.forEach((doc) => {
        moviesData.push({ ...doc.data(), id: doc.id });
      });
      // Sort movies by date added
      moviesData.sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));
      setMovies(moviesData);
      setIsLoading(false);
    }, (error) => {
      console.error("Firebase snapshot error:", error);
      alert("Could not fetch movies. Check console for errors. This is likely a Firebase security rules issue.");
      setIsLoading(false);
    });

    return () => unsubscribe(); // Cleanup listener on unmount
  }, [isGuest]);

  const watchedMovies = movies.filter(movie => movie.status === 'watched');
  const wantToWatchMovies = movies.filter(movie => movie.status === 'want-to-watch');

  const handleAddMovie = async (movieData) => {
    if (isGuest) return; // Disable for guest users
    try {
      await addDoc(collection(db, 'movies'), {
        ...movieData,
        ratings: {},
        dateAdded: new Date().toISOString()
      });
      setShowAddModal(false);
    } catch (e) {
      console.error("Error adding document: ", e);
    }
  };

  const handleUpdateRating = async (movieId, username, rating) => {
    if (isGuest) return; // Disable for guest users
    const movieDocRef = doc(db, 'movies', movieId);
    const movie = movies.find(m => m.id === movieId);
    try {
      await updateDoc(movieDocRef, {
        ratings: {
          ...movie.ratings,
          [username]: rating
        }
      });
    } catch (e) {
      console.error("Error updating rating: ", e);
    }
  };

  const handleMoveToWatched = async (movieId) => {
    if (isGuest) return; // Disable for guest users
    const movieDocRef = doc(db, 'movies', movieId);
    try {
      await updateDoc(movieDocRef, {
        status: 'watched',
        watchedOn: new Date().getFullYear() // Set default watched year
      });
    } catch (e) {
      console.error("Error moving movie: ", e);
    }
  };

  const handleMoveToWantToWatch = async (movieId) => {
    if (isGuest) return; // Disable for guest users
    const movieDocRef = doc(db, 'movies', movieId);
    try {
      await updateDoc(movieDocRef, {
        status: 'want-to-watch'
      });
    } catch (e) {
      console.error("Error moving movie: ", e);
    }
  };

  const handleDeleteMovie = async (movieId) => {
    if (isGuest) return; // Disable for guest users
    try {
      await deleteDoc(doc(db, 'movies', movieId));
    } catch (e) {
      console.error("Error deleting movie: ", e);
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>{isGuest ? "🎬 Movie Collection Demo" : "🎬 Jackson & Genna's Movies"}</h1>
          {isGuest && (
            <div className="demo-notice">
              <span>📺 Demo Mode - Read Only</span>
            </div>
          )}
          <div className="header-actions">
            <span className="welcome-text">Welcome, {user.displayName}!</span>
            <button onClick={onLogout} className="logout-button">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="dashboard-controls">
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'watched' ? 'active' : ''}`}
              onClick={() => setActiveTab('watched')}
            >
              Watched ({watchedMovies.length})
            </button>
            <button 
              className={`tab ${activeTab === 'want-to-watch' ? 'active' : ''}`}
              onClick={() => setActiveTab('want-to-watch')}
            >
              Want to Watch ({wantToWatchMovies.length})
            </button>
          </div>
          
          {!isGuest && (
            <button 
              className="add-movie-button"
              onClick={() => setShowAddModal(true)}
            >
              + Add Movie
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading movies...</p>
          </div>
        ) : (
          <>
            <div className="movies-grid">
              {activeTab === 'watched' && watchedMovies.map(movie => (
                <MovieCard 
                  key={movie.id} 
                  movie={movie} 
                  currentUser={user}
                  onUpdateRating={handleUpdateRating}
                  onMoveToWantToWatch={handleMoveToWantToWatch}
                  onDeleteMovie={handleDeleteMovie}
                  showRatings={true}
                  isGuest={isGuest}
                />
              ))}
              
              {activeTab === 'want-to-watch' && wantToWatchMovies.map(movie => (
                <MovieCard 
                  key={movie.id} 
                  movie={movie} 
                  currentUser={user}
                  onMoveToWatched={handleMoveToWatched}
                  onDeleteMovie={handleDeleteMovie}
                  showRatings={false}
                  isGuest={isGuest}
                />
              ))}
            </div>

            {((activeTab === 'watched' && watchedMovies.length === 0) || 
              (activeTab === 'want-to-watch' && wantToWatchMovies.length === 0)) && (
              <div className="empty-state">
                <p>No movies in this category yet.</p>
                {!isGuest && (
                  <button 
                    className="add-first-movie"
                    onClick={() => setShowAddModal(true)}
                  >
                    Add your first movie
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {showAddModal && !isGuest && (
        <AddMovieModal 
          onClose={() => setShowAddModal(false)}
          onAddMovie={handleAddMovie}
        />
      )}
    </div>
  );
};

export default Dashboard; 